import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Select from 'react-select';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';

import {
  BG_1,
  BORDER_1,
  BORDER_2,
  FG_1,
  FG_2,
  FG_3,
  PRIMARY_COLOR,
  RADIUS_LG,
  SHADOW_SM,
} from 'components/upb_viewer_beta/css/betaTokens';
import { DP_METRICS, TREND_RANGES, bandTier } from 'components/upb_match_explorer/constants';
import { fmtInt, fmtPct } from 'components/upb_match_explorer/format';
import MetricTrendCard from 'components/upb_match_explorer/MetricTrendCard';
import useUpbMatchPartner from 'components/upb_match_explorer/hooks/useUpbMatchPartner';
import useUpbMatchPartnerTrend from 'components/upb_match_explorer/hooks/useUpbMatchPartnerTrend';

function DpMetricsView({ entityId }) {
  const [range, setRange] = useState('7d');
  const { data: partner, loading, error, refetch } = useUpbMatchPartner({ entityId });
  const { data: trend } = useUpbMatchPartnerTrend({ entityId, range });

  if (loading) {
    return (
      <div className={css(styles.center)}>
        <Spinner animation="border" role="status" size="sm" />
      </div>
    );
  }

  // Once loading settles, an error (or a still-empty partner, e.g. a 404) needs
  // its own state — otherwise the spinner above would keep rendering forever.
  if (error || !partner) {
    return (
      <Alert variant="danger" className={css(styles.errorAlert)}>
        Couldn&apos;t load this partner
        {error?.response?.data?.message ? `: ${error.response.data.message}` : '.'}{' '}
        <Button variant="link" className="p-0 align-baseline" onClick={() => refetch()}>
          Retry
        </Button>
      </Alert>
    );
  }

  const stats = partner.stats || {};
  const series = trend?.series || {};
  const tier = bandTier(partner.band);
  const initial = (partner.name || '?').trim().charAt(0).toUpperCase();
  const matched = stats.UPB_MATCHED_COUNT;
  const denominator = stats.TOTAL_ACTIVE_PRODUCT_COUNT;

  return (
    <div>
      <div className={css(styles.header)}>
        <div className={css(styles.avatar)}>{initial}</div>
        <div className={css(styles.headerText)}>
          <h2 className={css(styles.name)}>{partner.name}</h2>
          <div className={css(styles.meta)}>
            <span>
              CND ID <b>{partner.cnd_id}</b>
            </span>
            <span
              className={css(styles.chip)}
              style={{ backgroundColor: tier.bg, color: tier.text }}
            >
              <span className={css(styles.dot)} style={{ backgroundColor: tier.color }} />
              {fmtPct(partner.match_pct)} UPB match
            </span>
          </div>
        </div>
      </div>

      <div className={css(styles.hero)}>
        <div className={css(styles.heroTop)}>
          <span className={css(styles.heroLabel)}>UPB match rate</span>
          <span className={css(styles.heroPct)} style={{ color: tier.color }}>
            {fmtPct(partner.match_pct)}
          </span>
        </div>
        <div className={css(styles.heroTrack)}>
          <div
            className={css(styles.heroFill)}
            style={{ width: `${partner.match_pct}%`, backgroundColor: tier.color }}
          />
        </div>
        <div className={css(styles.heroSub)}>
          <b>{fmtInt(matched)}</b> of <b>{fmtInt(denominator)}</b> active products linked to a UPB
          SKU.
        </div>
      </div>

      <div className={css(styles.trendBar)}>
        <div>
          <h3 className={css(styles.trendTitle)}>Metric trends</h3>
          <span className={css(styles.trendNote)}>Daily snapshots</span>
        </div>
        <div className={css(styles.rangeSelect)}>
          <Select
            classNamePrefix="themed_select"
            isSearchable={false}
            options={TREND_RANGES}
            value={TREND_RANGES.find(option => option.value === range)}
            onChange={option => setRange(option.value)}
          />
        </div>
      </div>

      <div className={css(styles.grid)}>
        {DP_METRICS.map(metric => (
          <MetricTrendCard key={metric.key} metric={metric} series={series[metric.key]} />
        ))}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  center: { display: 'flex', justifyContent: 'center', padding: 60, color: PRIMARY_COLOR },
  errorAlert: { margin: '20px 0' },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
    background: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_SM,
    padding: '20px 22px',
    marginBottom: 20,
  },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: 13,
    flexShrink: 0,
    background: `linear-gradient(135deg, ${PRIMARY_COLOR}, #4D3CA8)`,
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 20,
    fontWeight: 700,
  },
  headerText: { flex: 1, minWidth: 200 },
  name: { margin: 0, fontSize: 21, fontWeight: 700, color: FG_1 },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 6,
    flexWrap: 'wrap',
    color: FG_3,
    fontSize: 12.5,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 12,
    fontWeight: 600,
    padding: '4px 9px',
    borderRadius: 20,
  },
  dot: { width: 8, height: 8, borderRadius: '50%' },
  hero: {
    background: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_SM,
    padding: '20px 22px',
    marginBottom: 20,
  },
  heroTop: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 10,
  },
  heroLabel: { fontSize: 13, color: FG_2, fontWeight: 600 },
  heroPct: { fontSize: 30, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  heroTrack: {
    position: 'relative',
    height: 16,
    background: BORDER_2,
    borderRadius: 9,
    overflow: 'hidden',
  },
  heroFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: 9,
    transition: 'width 0.5s ease',
  },
  heroSub: { marginTop: 12, fontSize: 12.5, color: FG_3 },
  trendBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 14,
  },
  trendTitle: { fontSize: 15, fontWeight: 600, color: FG_1, margin: 0 },
  trendNote: { fontSize: 12, color: FG_3 },
  rangeSelect: { minWidth: 180, border: `1px solid ${BORDER_1}`, borderRadius: 9 },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 14,
    '@media (max-width: 900px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
    '@media (max-width: 560px)': { gridTemplateColumns: '1fr' },
  },
});

DpMetricsView.propTypes = {
  entityId: PropTypes.number.isRequired,
};

export default DpMetricsView;
